<template>
  <div>
    <app-spinner v-if="!briefing_data" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">
            {{ briefing_data.name }}
          </h1>
          <template v-if="briefing_data.speech">
            <h2 class="title">Speech:</h2>
            <div class="manager-title">
              <div class="manager-title__photo">
                <img :src="getUserAvatar(briefing_data.speech.crop.crop_url)" alt="Avatar">
              </div>
              <span class="manager-title__name">{{ briefing_data.speech.full_name }}</span>
            </div>
          </template>
          <template>
            <h2 class="title">Managers:</h2>
            <div v-for="manager of briefing_data.managers" :key="manager.id" class="manager-title">
              <div class="manager-title__photo">
                <img :src="getUserAvatar(manager.crop.crop_url)" alt="Avatar">
              </div>
              <span class="manager-title__name">{{ manager.full_name }}</span>
            </div>
          </template>
        </app-cells>
        <app-button v-if="is_general_manager || is_operations_supervisor" @click.native="onShowDeleteModal" theme="error">Delete</app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <button v-if="is_general_manager || is_operations_supervisor" @click="onShowEventModal" class="info-cards__control">
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span> <span>{{ dateFormat(briefing_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Location:</span><span>{{ briefing_data.location.short_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Start:</span> <span>{{ briefing_data.time_start }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="briefing_data.notes"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Notes"
          :readonly="!is_general_manager && !is_operations_supervisor"
        />
      </app-cells>
    </template>
    <app-tabs :tabs="tabs" />
    <transition name="fade" mode="out-in">
      <router-view @update-info="getInfo" />
    </transition>
    <modal
      name="event-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideEventModal" class="popup__close" type="button"></button>
        <div class="popup__title">Updating briefing</div>
        <div class="popup__body">
          <app-grid>
            <template #item-1>
              <app-form-group>
                <app-cells :indent="false">
                  <app-checkbox
                    v-model="form.briefing"
                    label="Briefing"
                  />
                  <app-checkbox
                    v-model="form.eq_check"
                    label="Equipment check"
                  />
                </app-cells>
              </app-form-group>
              <app-form-group label="Date" required>
                <app-date-picker
                  v-model="form.date"
                  placeholder="Date"
                  :error="$v.form.date.$error"
                />
                <template #error>
                  <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                </template>
              </app-form-group>
              <app-cells :indent="false" :wrap="false">
                <app-form-group label="Time start" label-for="time_start" required>
                  <app-input
                    v-model="form.time_start"
                    v-mask="'99:99'"
                    placeholder="Time start"
                    autocomplete="off"
                    id="time_start"
                    name="time_start"
                    @paste.native.prevent
                    :error="$v.form.time_start.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.time_start.$dirty && !$v.form.time_start.required">Required field</div>
                    <div v-if="$v.form.time_start.$dirty && !$v.form.time_start.timeRegex">Wrong time</div>
                  </template>
                </app-form-group>
                <app-form-group label="Time end" label-for="time_end" required>
                  <app-input
                    v-model="form.time_end"
                    v-mask="'99:99'"
                    placeholder="Time end"
                    autocomplete="off"
                    id="time_end"
                    name="time_end"
                    @paste.native.prevent
                    :error="$v.form.time_end.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.time_end.$dirty && !$v.form.time_end.required">Required field</div>
                    <div v-if="$v.form.time_end.$dirty && !$v.form.time_end.timeRegex">Wrong time</div>
                  </template>
                </app-form-group>
              </app-cells>
              <app-form-group label="Location" required>
                <app-select
                  @input="onLocationSelected"
                  @search="onLocationSearch"
                  v-model="form.location"
                  :options="location_options"
                  :clearable="false"
                  placeholder="Location"
                  :error="$v.form.location.$error"
                />
                <template #error>
                  <div v-if="$v.form.location.$dirty && !$v.form.location.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <template #item-2>
              <app-form-group label="Managers" required>
                <app-select
                  v-model="form.managers"
                  :options="manager_options"
                  :display-field="option => `${option.first_name} ${option.last_name}`"
                  label="first_name"
                  placeholder="Managers"
                  multiple
                  :error="$v.form.managers.$error"
                />
                <template #error>
                  <div v-if="$v.form.managers.$dirty && !$v.form.managers.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group v-if="form.briefing" label="Speech" required>
                <app-select
                  v-model="form.speech"
                  :options="form.managers"
                  :clearable="false"
                  :display-field="option => `${option.first_name} ${option.last_name}`"
                  label="first_name"
                  placeholder="Speech"
                  :error="$v.form.speech.$error"
                />
                <template #error>
                  <div v-if="$v.form.speech.$dirty && !$v.form.speech.required">Required field</div>
                </template>
              </app-form-group>
            </template>
          </app-grid>
          <app-cells position="center" :indent="false">
            <app-button
              @click.native="onCheckForm"
              ref="createEvent"
              :disabled="$v.form.$error"
            >
              Update briefing
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideDeleteModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete briefing
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this briefing?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideDeleteModal" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteBriefing" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import {
  deleteClimbingBriefing,
  getClimbingBriefing,
  getGoogleSearch,
  getUsers,
  patchClimbingBriefing,
  postLocation,
} from '@/http'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import { format, parse } from 'date-fns'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'ClimbingBriefingsDetailLayout',
  components: { AppTabs },
  data() {
    return {
      tabs: [
        { name: 'climbing-briefings-detail-clients', label: 'Clients' },
        { name: 'climbing-briefings-detail-invoices', label: 'Invoices' },
      ],
      briefing_data: null,
      form: {},
      manager_options: [],
      location_options: [],
    }
  },
  validations: {
    form: {
      speech: {
        required: requiredIf(function() {
          return this.form.briefing
        })
      },
      time_start: { timeRegex, required },
      time_end: { timeRegex, required },
      managers: { required },
      date: { required },
      location: { required },
      clients: { required },
    },
  },
  created() {
    this.getInfo()
    getUsers({ group: 'operations_manager,general_manager,operations_supervisor', is_active: true, is_superuser: false }).then(response => { this.manager_options = response.data })
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    }
  },
  methods: {
    getInfo() {
      getClimbingBriefing(this.$route.params.id)
        .then(response => {
          this.briefing_data = response.data
          this.form = {
            briefing: response.data.briefing,
            eq_check: response.data.eq_check,
            clients: response.data.clients,
            location: response.data.location,
            date: response.data.date,
            managers: response.data.managers,
            speech: response.data.speech,
            time_start: response.data.time_start,
            time_end: response.data.time_end,
          }
        })
    },
    onUpdateNotes() {
      patchClimbingBriefing(this.$route.params.id, { notes: this.briefing_data.notes })
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    onShowDeleteModal() {
      this.$modal.show('delete-modal')
    },
    onHideDeleteModal() {
      this.$modal.hide('delete-modal')
    },
    onShowEventModal() {
      this.$modal.show('event-modal')
    },
    onHideEventModal() {
      this.$modal.hide('event-modal')
      this.$v.form.$reset()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.createEvent.preload = true
      patchClimbingBriefing(this.$route.params.id, this.normalizeForm())
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Briefing updated',
          })
          this.onHideEventModal()
        })
        .catch(() => {
          this.$refs.createEvent.preload = false
        })
    },
    normalizeForm() {
      const form = {...this.form}
      form.clients = form.clients.map(item => ({ id: item.id, climbing_group: item.group.id }))
      form.location = form.location.id
      form.managers = form.managers.map(item => item.id)
      if (!form.briefing) form.speech = null
      else form.speech = form.speech.id
      return form
    },
    onDeleteBriefing() {
      this.$refs.delete.preload = true
      deleteClimbingBriefing(this.$route.params.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Briefing deleted',
          })
          this.$router.push({name: 'climbing-briefings-list'})
        })
        .catch(() => {
          this.$refs.delete.preload = false
        })
    },
    onLocationSelected({ name, place_id }) {
      postLocation({ name, place_id }).then(res => {
        this.form.location = res.data
      })
    },
    onLocationSearch(search, loading) {
      loading(true)
      this.searchLocation(loading, search, this)
    },
    searchLocation: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.location_options = []
      } else {
        getGoogleSearch(search).then(response => {
          vm.location_options = response.data
          loading(false)
        })
      }
    }, 350),
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
  }
}
</script>
